import React from "react";

import { Box, Grid, Typography } from "@mui/material";

import Tooth from "./Tooth";

import { ARCH_ROWS, DECIDUOUS_TO_PERMANENT, PERMANENT_TO_DECIDUOUS } from "../../../../config/constants/Constants";

export default function MissingAndImplantsConfirm({ state, setState }) {
    // chartingState: chartingTable { [toothNumber: string]: { missing: bool, implant: bool, ... } }
    // setChartingState: fn

    // Stable across renders so the memoized Tooth squares are not invalidated on every tap.
    // The current missing/implant flags are read inside the updater to avoid depending on `state`,
    // which keeps rapid taps from acting on a stale snapshot.
    const handleToggle = React.useCallback(
        (num) => {
            setState((prev) => {
                const isMissing = prev[num]?.missing;
                const isImplant = prev[num]?.implant;

                // Deciduous teeth: missing -> neither
                if (num > 50) {
                    if (!isMissing) {
                        return { ...prev, [num]: { ...prev[num], missing: true, implant: false } };
                    }

                    const newState = {
                        ...prev,
                        [num]: { ...prev[num], missing: false, implant: false }
                    };

                    // Deciduous tooth is being marked as NOT missing (i.e., it remains)
                    // Auto-set corresponding permanent tooth as missing
                    const permanentTooth = DECIDUOUS_TO_PERMANENT[num];
                    if (permanentTooth && newState[permanentTooth]) {
                        newState[permanentTooth] = {
                            ...newState[permanentTooth],
                            missing: true,
                            implant: false
                        };
                    }

                    return newState;
                }

                // Permanent teeth: missing -> implant -> neither
                if (isMissing) {
                    const newState = {
                        ...prev,
                        [num]: { ...prev[num], missing: false, implant: true }
                    };

                    // If a permanent tooth is marked as NOT missing (i.e., it remains),
                    // automatically set its corresponding deciduous tooth as missing
                    const deciduousTooth = PERMANENT_TO_DECIDUOUS[num];
                    if (deciduousTooth && newState[deciduousTooth]) {
                        newState[deciduousTooth] = {
                            ...newState[deciduousTooth],
                            missing: true,
                            implant: false
                        };
                    }

                    return newState;
                }

                if (isImplant) {
                    return { ...prev, [num]: { ...prev[num], missing: false, implant: false } };
                }

                return { ...prev, [num]: { ...prev[num], missing: true, implant: false } };
            });
        },
        [setState]
    );

    return (
        <Box sx={{ width: "100%", mt: 1, mb: 1 }}>
            <Typography variant="h5" align="center" sx={{ mb: 2 }}>
                Dental Chart - confirm missing teeth and implants
            </Typography>

            <Typography variant="body2" align="center" sx={{ mb: 2, color: "text.secondary" }}>
                Tap a tooth to cycle: present → missing → implant → present. Deciduous teeth cycle between present and missing only.
            </Typography>

            <Grid container direction="column" spacing={4} justifyContent="center" alignItems="center">
                {ARCH_ROWS.map(([right, left], idx) => (
                    <Grid item key={idx}>
                        <Grid
                            container
                            spacing={1}
                            justifyContent="center"
                            alignItems="center"
                            // Stack right/left vertically on mobile
                            // Side by side on wider screens
                            sx={{
                                flexDirection: { xs: "column", sm: "row" },
                                flexWrap: { xs: "nowrap", sm: "wrap" }
                            }}>
                            {/* Right side */}
                            <Grid item>
                                <Grid container spacing={1} direction="row" justifyContent="center" wrap="wrap">
                                    {right.map((num) => (
                                        <Grid item key={num}>
                                            <Tooth num={num} missing={!!state[num]?.missing} implant={!!state[num]?.implant} handleToggle={handleToggle} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>

                            {/* Gap */}
                            <Box
                                sx={{
                                    width: { xs: 0, sm: 30 },
                                    height: { xs: 16, sm: "auto" },
                                    my: { xs: 1, sm: 0 }
                                }}
                            />

                            {/* Left side */}
                            <Grid item>
                                <Grid container spacing={1} direction="row" justifyContent="center" wrap="wrap">
                                    {left.map((num) => (
                                        <Grid item key={num}>
                                            <Tooth num={num} missing={!!state[num]?.missing} implant={!!state[num]?.implant} handleToggle={handleToggle} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}
